/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 2: what the Today page derives from one capped query of the
 * week's classes. Pure, so each rule is testable without Firestore.
 */
import type { Class } from "@lemonadetraining/contracts";
import { classLifecycle } from "./classLifecycle";
import {
  activeMemberDayKey,
  activeMemberMonthKey,
  type ActiveMemberHistoryDoc,
} from "@lemonadetraining/contracts";
import { gymDateParts, type GymWindow } from "./gymTime";

export type TodayClass = Class & { id: string };

const time = (value: unknown): number => {
  if (value instanceof Date) return value.getTime();
  const maybe = value as { toDate?: () => Date } | null;
  if (maybe && typeof maybe.toDate === "function") return maybe.toDate().getTime();
  return new Date(value as string).getTime();
};

const within = (cls: TodayClass, window: GymWindow): boolean => {
  const start = time(cls.startTime);
  return start >= window.start.getTime() && start < window.end.getTime();
};

/** Today's classes on the gym's calendar, in start order. */
export const classesToday = (classes: TodayClass[], today: GymWindow): TodayClass[] =>
  classes.filter((cls) => within(cls, today)).sort((a, b) => time(a.startTime) - time(b.startTime));

/** Classes still to come in the next 7 days with nobody coaching them. */
export const classesWithoutCoach = (classes: TodayClass[], now: Date): TodayClass[] => {
  const horizon = now.getTime() + 7 * 24 * 60 * 60 * 1000;
  return classes
    .filter(
      (cls) =>
        !cls.coachUID &&
        cls.status !== "cancelled" &&
        time(cls.endTime) > now.getTime() &&
        time(cls.startTime) < horizon
    )
    .sort((a, b) => time(a.startTime) - time(b.startTime));
};

const waitingLaterToday = (cls: TodayClass, now: Date): boolean =>
  cls.status !== "cancelled" && time(cls.endTime) > now.getTime() && (cls.waitlistCount ?? 0) > 0;

const isFull = (cls: TodayClass): boolean =>
  (cls.capacity ?? 0) > 0 && (cls.currentBookings ?? 0) >= (cls.capacity ?? 0);

/** Classes later today that are full and have people waiting. */
export const fullWithWaitlist = (todays: TodayClass[], now: Date): TodayClass[] =>
  todays.filter((cls) => waitingLaterToday(cls, now) && isFull(cls));

/** People are waiting although places are free: the promotion did not happen. */
export const openWithWaitlist = (todays: TodayClass[], now: Date): TodayClass[] =>
  todays.filter((cls) => waitingLaterToday(cls, now) && !isFull(cls));

/** People waiting across those classes. */
export const waitingCount = (classes: TodayClass[]): number =>
  classes.reduce((sum, cls) => sum + (cls.waitlistCount ?? 0), 0);

/**
 * Share of places booked across the week's classes that go ahead. Null when
 * there is nothing to measure, so the page says "no classes" rather than 0%.
 */
export const bookedShare = (classes: TodayClass[], week: GymWindow): number | null => {
  let booked = 0;
  let capacity = 0;
  for (const cls of classes) {
    if (cls.status === "cancelled" || !within(cls, week)) continue;
    booked += Math.min(cls.currentBookings ?? 0, cls.capacity ?? 0);
    capacity += cls.capacity ?? 0;
  }
  return capacity > 0 ? booked / capacity : null;
};

export type TodayClassState = "upcoming" | "now" | "attendance-pending" | "done" | "cancelled";

/** How a row reads on Today: the lifecycle, plus "now" while it runs. */
export const todayClassState = (cls: TodayClass, now: Date): TodayClassState => {
  const lifecycle = classLifecycle(cls, now);
  if (lifecycle === "cancelled") return "cancelled";
  if (lifecycle === "completed") return "done";
  if (lifecycle === "attendance-pending") return "attendance-pending";
  return time(cls.startTime) <= now.getTime() ? "now" : "upcoming";
};

/** The next class that has not ended, for "Next up". */
export const nextUp = (todays: TodayClass[], now: Date): TodayClass | null =>
  todays.find((cls) => cls.status !== "cancelled" && time(cls.endTime) > now.getTime()) ?? null;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "in 48 minutes", "in 1 hour 30 minutes", "now". */
export const untilLabel = (cls: TodayClass, now: Date): string => {
  const minutes = Math.ceil((time(cls.startTime) - now.getTime()) / 60000);
  if (minutes <= 0) return "now";
  if (minutes < 60) return `in ${plural(minutes, "minute")}`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `in ${plural(hours, "hour")}${rest ? ` ${plural(rest, "minute")}` : ""}`;
};

export const startTimeOf = (cls: TodayClass): Date => new Date(time(cls.startTime));

export interface SetupSignals {
  hasDetails: boolean | null;
  hasClassTypes: boolean | null;
  hasMemberships: boolean | null;
  hasCoaches: boolean | null;
  hasPayments: boolean | null;
  hasTerms: boolean | null;
}

export type SetupKey = keyof SetupSignals;

export interface SetupStep {
  key: SetupKey;
  label: string;
  /** Path under the dashboard segment. */
  to: string;
  action: string;
  /** null = could not check. */
  done: boolean | null;
}

const SETUP: Array<Omit<SetupStep, "done">> = [
  { key: "hasDetails", label: "Add your gym details", to: "gym-settings", action: "Add" },
  {
    key: "hasClassTypes",
    label: "Create class types",
    to: "box-portal/booking?tab=class-types",
    action: "Create",
  },
  {
    key: "hasMemberships",
    label: "Add memberships",
    to: "gym-memberships",
    action: "Add",
  },
  {
    key: "hasCoaches",
    label: "Invite your coaches",
    to: "team",
    action: "Invite",
  },
  {
    key: "hasPayments",
    label: "Connect payments",
    to: "gym-settings?section=payments",
    action: "Set up",
  },
  {
    key: "hasTerms",
    label: "Publish your terms",
    to: "gym-settings?section=terms",
    action: "Write",
  },
];

export const setupSteps = (signals: SetupSignals): SetupStep[] =>
  SETUP.map((step) => ({ ...step, done: signals[step.key] }));

/** The checklist leaves once every step is known to be done. */
export const setupComplete = (steps: SetupStep[]): boolean => steps.every((s) => s.done === true);

export type TodayRole = "owner" | "coach" | "independent";

export interface TodayRoleInput {
  hasBox: boolean;
  isBoxAdmin: boolean;
  isBoxCoach: boolean;
  coachesIndependently: boolean;
}

/**
 * Which Today a person gets, or null for none. Platform admins get one only
 * through a real role at a gym or their own coaching, never by being admins.
 */
export const todayRole = (a: TodayRoleInput): TodayRole | null => {
  if (a.hasBox && a.isBoxAdmin) return "owner";
  if (a.hasBox && a.isBoxCoach) return "coach";
  if (a.coachesIndependently) return "independent";
  return null;
};

interface AccessForToday {
  boxID: string | null;
  isBoxAdmin: boolean;
  isBoxCoach: boolean;
  claims: { coach?: boolean } | null;
}

/** Holds the coach claim outside a pure box-coach role: owns a coaching business. */
export const coachesIndependently = (a: AccessForToday): boolean =>
  a.claims?.coach === true && !(a.isBoxCoach && !a.isBoxAdmin);

/** The one routing rule HomePage, TodayPage and the sidebar all follow. */
export const todayRoleFor = (a: AccessForToday): TodayRole | null =>
  todayRole({
    hasBox: Boolean(a.boxID),
    isBoxAdmin: a.isBoxAdmin,
    isBoxCoach: a.isBoxCoach,
    coachesIndependently: coachesIndependently(a),
  });

/** The gym-local month keys, oldest first, ending with the current month. */
export const recentMonthKeys = (now: Date, timeZone: string, count: number): string[] => {
  const { year, month } = gymDateParts(now, timeZone);
  return Array.from({ length: count }, (_, i) => {
    const back = count - 1 - i;
    const total = year * 12 + (month - 1) - back;
    return activeMemberMonthKey(Math.floor(total / 12), (total % 12) + 1);
  });
};

export interface ActiveMemberPoint {
  /** "YYYY-MM-DD" in the gym's zone. */
  date: string;
  count: number;
}

/**
 * The recorded days in order, with today's live count as the last point so
 * the line always ends at the number shown above it, even before the first
 * nightly record exists.
 */
export const activeMemberSeries = (
  docs: Array<ActiveMemberHistoryDoc | null>,
  now: Date,
  timeZone: string,
  liveCount: number
): ActiveMemberPoint[] => {
  const points: ActiveMemberPoint[] = [];
  for (const d of docs) {
    if (!d || typeof d.month !== "string") continue;
    for (const [day, count] of Object.entries(d.days ?? {})) {
      if (typeof count === "number" && Number.isFinite(count)) {
        points.push({ date: `${d.month}-${day}`, count });
      }
    }
  }
  points.sort((a, b) => a.date.localeCompare(b.date));
  const { year, month, day } = gymDateParts(now, timeZone);
  const today = `${activeMemberMonthKey(year, month)}-${activeMemberDayKey(day)}`;
  const rest = points.filter((p) => p.date < today);
  return [...rest, { date: today, count: liveCount }];
};
